import { CaretDown, CaretUp } from '@carbon/icons-react'
import React, { useEffect, useMemo, useState } from 'react'

import BasicDetailsCard from '../../components/SalesforceAudit/common/BasicDetailsCard'
import { DDSTypography } from 'den-design-system'
import NoRecord from '../../components/common/NoContent/NoRecord'
import NoRecordData from '../../assets/images/setting.svg'
import NoRecordImage from '../../assets/images/folder-not-found 1.svg'
import SFVerticalBasicDetailsCard from '../../components/SalesforceAudit/SFVerticalBasicCardDetails'
import { SalesforceAuditStore } from '../../store/SalesforceAuditStore'
import Search from '../../components/New/Search/Search'
import Table from '../../components/common/DDS/Table/Table'
import Tooltip from '../../components/New/Tooltip/Tooltip'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { paginate } from '../../utils/CommonUtils'
import pallete from '../../global/newPallete'
import { replaceUrl } from '../../utils/UrlUtils'
import routeConstants from '../../constants/RouteConstants'
import { timeFormats } from '../../enum/TIME'
import { useParams } from 'react-router-dom'
import { useStoreContext } from '../../store/StoreContext'

const SFProfileDetails: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const params = useParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeSearchTerm, setActiveSearchTerm] = useState('')
  const [searchColumn, setSearchColumn] = useState('all')
  const [showDropdown, setShowDropdown] = useState(false)
  const { salesforceAuditStore } = useStoreContext()
  const store = useStoreContext()
  const data = salesforceAuditStore.getProfileById(params.id!)
  const profileData = salesforceAuditStore.getProfilePermissionsData(params.id!)
  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: 'asc' | 'desc' | null
  }>({
    key: null,
    direction: null
  })

  const toggleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return { key: null, direction: null }
    })
  }

  const filteredData = useMemo(() => {
    const trimmedSearch = activeSearchTerm
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
    if (!trimmedSearch) return profileData
    const lowerSearch = trimmedSearch.toLowerCase()
    return profileData.filter((item: any) => {
      const name = item?.name?.toLowerCase() || ''
      const access = item?.access?.toLowerCase() || ''
      switch (searchColumn) {
        case 'name':
          return name.includes(lowerSearch)
        case 'access':
          return access.includes(lowerSearch)
        case 'all':
        default:
          return name.includes(lowerSearch) || access.includes(lowerSearch)
      }
    })
  }, [profileData, activeSearchTerm, searchColumn])

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData
    return [...filteredData].sort((a: any, b: any) => {
      const valA = a?.[sortConfig.key!] || ''
      const valB = b?.[sortConfig.key!] || ''
      if (!isNaN(Number(valA)) && !isNaN(Number(valB))) {
        return sortConfig.direction === 'asc'
          ? Number(valA) - Number(valB)
          : Number(valB) - Number(valA)
      }
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortConfig.direction === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA)
      }
      return 0
    })
  }, [filteredData, sortConfig, salesforceAuditStore])

  const count = sortedData.reduce(
    (acc, item) => {
      if (item.access === 'Yes') acc.yes++
      else if (item.access === 'No') acc.no++
      return acc
    },
    { yes: 0, no: 0 }
  )

  const Columns = [
    {
      columnHeaderStyle: { width: '70%' },
      columnDataStyle: { width: '70%' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Permission Name
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('name')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'name' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'name' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'name',
      render: (text: any) => (
        <div className='flex justify-start max-w-[70%] text-start row items-start '>
          <div>
            <Tooltip
              id={`tooltip-name-${text}`}
              label={text}
              className={`override-tooltip-arrow max-w-[90%] break-all ${
                typeof text === 'string' && text.length > 24 ? text : '!hidden'
              }`}
            >
              <div>
                <DDSTypography.Paragraph
                  size='para'
                  variant='medium'
                  color={pallete.colors.textDark4}
                  className='tooltip-container sm:block overflow-hidden truncate max-w-[180px]'
                >
                  {text || text === 0 ? text : '-'}
                </DDSTypography.Paragraph>
              </div>
            </Tooltip>
          </div>
        </div>
      )
    },
    {
      columnHeaderStyle: { width: '30%' },
      columnDataStyle: { width: '30%' },
      heading: (
        <div className='flex whitespace-nowrap space-x-[8px] text-left justify-left items-left'>
          <div>
            <DDSTypography.Paragraph
              variant='semiBold'
              size='caption'
              className='text-left flex whitespace-nowrap'
              color={pallete.colors.textDark6}
            >
              Access
            </DDSTypography.Paragraph>
          </div>
          <div
            className='flex flex-col justify-left -space-y-[6px]'
            onClick={() => toggleSort('access')}
          >
            <CaretUp
              size={12}
              style={{
                color:
                  sortConfig.key === 'access' && sortConfig.direction === 'asc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
            <CaretDown
              size={12}
              style={{
                color:
                  sortConfig.key === 'access' && sortConfig.direction === 'desc'
                    ? pallete.colors.primary2
                    : pallete.colors.textDark6
              }}
              className='cursor-pointer select-none'
            />
          </div>
        </div>
      ),
      dataLabel: 'access',
      render: (text: any) => {
        return (
          <div className='flex justify-start text-start row items-start'>
            <DDSTypography.Paragraph
              size='para'
              variant='medium'
              style={{
                color:
                  text === 'Yes'
                    ? pallete.colors.warningGreen
                    : pallete.colors.warningRed2
              }}
            >
              {text}
            </DDSTypography.Paragraph>
          </div>
        )
      }
    }
  ]

  useEffect(() => {
    store.breadcrumbStore.setMultipleBreadcrumbsOptions([
      {
        label: 'Salesforce Audit',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_DASHBOARD,
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      },
      {
        label: 'Profiles',
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/profiles',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      },
      {
        label: data?.Name,
        link: replaceUrl(
          routeConstants.SALESFORCE_AUDIT_METADATA_OVERVIEW + '/profiles',
          store.scopeStore.getScope()['organization-id'] as string,
          store.scopeStore.getScope()['project-id'] as string,
          store.scopeStore.getScope()['environment-id'] as string
        )
      }
    ])
  }, [store.breadcrumbStore.getBreadcrumbsOptions()])

  return (
    <div className='flex flex-col w-full py-[24px]'>
      <div className='grid grid-cols-5 gap-[16px]'>
        <div className='col-span-2'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark3}
            className='mb-[8px]'
          >
            Basic Details
          </DDSTypography.Title>

          <SFVerticalBasicDetailsCard
            title='Basic Info'
            permissionSetNameTitle='User License'
            permissionSetNameData={data?.Name}
            activationRequiredTitle='Activation Required'
            activationRequiredData={data?.HasActivationRequired ? 'Yes' : 'No'}
            modifiedOnTitle='Modified On'
            modifiedOnData={moment(data?.LastModifiedDate).format(
              timeFormats.shortMonthDateYearTime
            )}
            modifiedByTitle='Modified By'
            modifiedByData={data?.LastModifiedBy?.Name}
            createdOnTitle='Created On'
            createdOnData={moment(data?.CreatedDate).format(
              timeFormats.shortMonthDateYearTime
            )}
            createdByTitle='Created By'
            createdByData={data?.CreatedBy?.Name}
            descriptionTitle='Description'
            descriptionData={data?.Description}
          />
        </div>

        {/* Right side - Permissions */}
        <div className='col-span-3'>
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.textDark3}
            className='mb-[8px]'
          >
            Permissions
          </DDSTypography.Title>

          <div className='grid grid-cols-3 gap-[16px]'>
            <BasicDetailsCard
              id={data?.id}
              heading='Total Permissions'
              content={sortedData?.length}
            />
            <BasicDetailsCard
              id={data?.id}
              isGreen={true}
              heading='Allowed Permissions'
              content={count?.yes}
            />
            <BasicDetailsCard
              id={data?.id}
              isRed={true}
              heading='Denied Permissions'
              content={count?.no}
            />
          </div>
          <div className='flex flex-col py-[24px]'>
            <div className='relative pb-[12px]'>
              <Search
                id='search'
                name='Search'
                placeholder='Search'
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value
                  setSearchTerm(value)
                  setShowDropdown(value.length > 0)
                  if (value.trim() === '') {
                    setActiveSearchTerm('')
                  }
                }}
                autoComplete='off'
                width='100%'
                onPressEnter={() => {
                  setActiveSearchTerm(searchTerm.trim())
                  setCurrentPage(1)
                  setShowDropdown(false)
                }}
                onBlur={() => {
                  setTimeout(() => {
                    setShowDropdown(false)
                    setCurrentPage(1)
                  }, 200)
                }}
              />

              {showDropdown && (
                <div className='absolute z-[99999] mt-[4px] w-full bg-white border border-gray-200 rounded-md shadow-md'>
                  {[
                    { label: 'Permission Name', value: 'name' },
                    { label: 'Access', value: 'access' },
                    { label: 'All Columns', value: 'all' }
                  ].map((option) => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setSearchColumn(option.value)
                        setCurrentPage(1)
                        setActiveSearchTerm(searchTerm)
                        setShowDropdown(false)
                      }}
                      className='px-4 py-2 cursor-pointer hover:bg-gray-100 text-sm text-gray-800'
                    >
                      <span className='font-mulish text-black'>
                        {searchTerm}
                      </span>
                      <span className='ml-[12px] px-[4px] py-[2px] rounded-md bg-gray-100 text-gray-600 text-xs font-medium'>
                        in: {option.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {sortedData?.length > 0 ? (
              <div className='max-h-[56.2vh] 2xl:max-h-[63vh] overflow-y-auto'>
                <Table
                  columns={Columns}
                  data={paginate(
                    sortedData,
                    currentPage,
                    SalesforceAuditStore.pageSize
                  )}
                  totalCount={sortedData?.length}
                  currentPage={currentPage}
                  rowsPerPage={SalesforceAuditStore.pageSize}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            ) : searchTerm?.trim() ? (
              <NoRecord
                imageSrc={NoRecordImage}
                style={{ height: '45vh' }}
                text={<div>No Profiles found for your search.</div>}
              />
            ) : (
              <NoRecord
                imageSrc={NoRecordData}
                style={{ height: '45vh' }}
                text={<div>No Profiles have been set up yet.</div>}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
export default observer(SFProfileDetails)
